import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, AlertTriangle, CheckCircle, Search, Filter } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { abidjanDistributionService } from '../../services/abidjanDistributionService';
import { realtimeService } from '../../services/realtimeService';
import { getValidStatusCodes, getStatusLabel } from '../../services/containerService';

/**
 * Abidjan Container Distribution Dashboard
 * Provides comprehensive oversight of container-based delivery operations in Abidjan
 */
export default function AbidjanContainerDistributionDashboard() {
  const navigate = useNavigate();
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [userRole, setUserRole] = useState('agent_abj');
  const [deliveryTasks, setDeliveryTasks] = useState([]);

  // Subscribe to real-time delivery tasks updates (filtered for Abidjan agent)
  useEffect(() => {
    if (!isAuthenticated || userRole !== 'agent_abj') return;

    const subscription = realtimeService?.subscribeToDeliveryTasks((payload) => {
      if (payload?.eventType === 'INSERT') {
        setDeliveryTasks(prev => [payload?.new, ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        setDeliveryTasks(prev => prev?.map(task =>
          task?.id === payload?.new?.id ? payload?.new : task
        ));
      } else if (payload?.eventType === 'DELETE') {
        setDeliveryTasks(prev => prev?.filter(task => task?.id !== payload?.old?.id));
      }
    });

    return () => {
      realtimeService?.unsubscribe('delivery-tasks-realtime');
    };
  }, [isAuthenticated, userRole]);

  // Subscribe to real-time containers updates (filtered for Abidjan)
  useEffect(() => {
    if (!isAuthenticated || userRole !== 'agent_abj') return;

    const subscription = realtimeService?.subscribeToContainers((payload) => {
      if (payload?.eventType === 'UPDATE') {
        setContainers(prev => prev?.map(container =>
          container?.id === payload?.new?.id ? payload?.new : container
        ));
      }
    });

    return () => {
      realtimeService?.unsubscribe('containers-realtime');
    };
  }, [isAuthenticated, userRole]);

  // Fetch containers on mount
  useEffect(() => {
    fetchContainers();
  }, []);

  const fetchContainers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await abidjanDistributionService?.getAbidjanContainers();
      setContainers(data);
    } catch (err) {
      setError(err?.message || 'Erreur lors du chargement des conteneurs');
      console.error('Error fetching containers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter containers based on search and status
  const filteredContainers = containers?.filter(container => {
    const matchesSearch = !searchTerm || 
      container?.containerCode?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      container?.vesselName?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    const matchesStatus = !statusFilter || 
      container?.statusLabel === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Navigate to container detail
  const handleContainerClick = (containerId) => {
    navigate(`/abidjan-container-detail-management/${containerId}`);
  };

  // Get status badge styling
  const getStatusBadge = (statusLabel) => {
    switch (statusLabel) {
      case 'EN_ATTENTE':
        return 'bg-yellow-100 text-yellow-800';
      case 'EN_DISTRIBUTION':
        return 'bg-blue-100 text-blue-800';
      case 'TERMINE':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Calculate progress percentage
  const calculateProgress = (delivered, total) => {
    if (!total) return 0;
    return Math.round((delivered / total) * 100);
  };

  // Update the getStatusColor function
  const getStatusColor = (status) => {
    const colors = {
      'AU_PORT_LE_HAVRE_ANVERS': 'bg-gray-100 text-gray-800',
      'EN_TRANSIT_MARITIME': 'bg-blue-100 text-blue-800',
      'ARRIVE_A_ABIDJAN': 'bg-purple-100 text-purple-800',
      'DEDOUANEMENT_EN_COURS': 'bg-yellow-100 text-yellow-800',
      'TRANSFERT_A_ENTREPOT': 'bg-orange-100 text-orange-800',
      'DISTRIBUTION_EN_COURS': 'bg-indigo-100 text-indigo-800',
      'CONTENEUR_TERMINE': 'bg-green-100 text-green-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Distribution Abidjan
          </h1>
          <p className="text-gray-600">
            Gestion des conteneurs et livraisons Abidjan
          </p>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Statut
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e?.target?.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Tous les statuts</option>
                {getValidStatusCodes()?.map(({ code, label }) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Rechercher par référence ou navire..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Refresh Button */}
            <button
              onClick={fetchContainers}
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Chargement...' : 'Actualiser'}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6">
            <p className="font-medium">Erreur</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement des conteneurs...</p>
          </div>
        )}

        {/* Containers Table - Desktop */}
        {!loading && filteredContainers?.length > 0 && (
          <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Référence Conteneur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Navire
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Livraisons
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progression
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Incidents
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredContainers?.map((container) => {
                  const progress = calculateProgress(container?.deliveredCount, container?.totalDeliveries);
                  return (
                    <tr 
                      key={container?.id}
                      onClick={() => handleContainerClick(container?.id)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Package className="w-5 h-5 text-green-600 mr-2" />
                          <span className="text-sm font-medium text-gray-900">
                            {container?.containerCode}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{container?.vesselName}</div>
                        <div className="text-sm text-gray-500">{container?.shippingLine}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(container?.status)}`}>
                          {getStatusLabel(container?.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {container?.deliveredCount} / {container?.totalDeliveries}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-32 bg-gray-200 rounded-full h-2 mr-2">
                            <div 
                              className="bg-green-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${progress}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-600">{progress}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {container?.incidentCount > 0 ? (
                          <div className="flex items-center text-red-600">
                            <AlertTriangle className="w-5 h-5 mr-1" />
                            <span className="text-sm font-medium">{container?.incidentCount}</span>
                          </div>
                        ) : (
                          <div className="flex items-center text-green-600">
                            <CheckCircle className="w-5 h-5 mr-1" />
                            <span className="text-sm">OK</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Containers Cards - Mobile */}
        {!loading && filteredContainers?.length > 0 && (
          <div className="md:hidden space-y-4">
            {filteredContainers?.map((container) => {
              const progress = calculateProgress(container?.deliveredCount, container?.totalDeliveries);
              return (
                <div 
                  key={container?.id}
                  onClick={() => handleContainerClick(container?.id)}
                  className="bg-white rounded-lg shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow"
                >
                  {/* Container Header */}
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {container?.container_number}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(container?.status)}`}>
                      {getStatusLabel(container?.status)}
                    </span>
                  </div>
                  {/* Vessel Info */}
                  <div className="text-sm text-gray-600 mb-3">
                    <div>{container?.vesselName}</div>
                    <div className="text-xs text-gray-500">{container?.shippingLine}</div>
                  </div>
                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Livraisons: {container?.deliveredCount} / {container?.totalDeliveries}</span>
                      <span>{progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  </div>
                  {/* Incidents */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Incidents:</span>
                    {container?.incidentCount > 0 ? (
                      <div className="flex items-center text-red-600">
                        <AlertTriangle className="w-4 h-4 mr-1" />
                        <span className="font-medium">{container?.incidentCount}</span>
                      </div>
                    ) : (
                      <div className="flex items-center text-green-600">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        <span>OK</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredContainers?.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucun conteneur trouvé
            </h3>
            <p className="text-gray-600">
              {searchTerm || statusFilter ? 
                'Essayez de modifier vos filtres de recherche' : 
                'Les conteneurs Abidjan apparaîtront ici'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}